import React, { useEffect, useState } from "react";
import { RoundBorderComponentContainer } from "@components/templates/layouts/round-border-component-container/round-border-component-container";
import { MainContainer } from "@components/templates/layouts/main-container/main-container";
import { Spacer16 } from "@components/atoms/spacer-16/spacer-16";

import { NodeManagerInfoBarPillsNav } from "@src/components/organisms/node-manager-info-bar-pills-nav/node-manager-info-bar-pills-nav";
import { RootState } from "@src/reducers/store";
import { useDispatch, useSelector } from "react-redux";
import { TreeStructure } from "@src/components/organisms/tree-structure/tree-structure";
import { BlueprintDashboard } from "@src/components/organisms/blueprint-dashboard/blueprint-dashboard";
import { AlarmList } from "@src/components/organisms/alarm-list/alarm-list";
import { OneShot } from "@src/components/organisms/one-shot/one-shot";
import { ShotTable } from "@src/components/organisms/shot-table/shot-table";
import { getNodeManager } from "@src/cloud-connect/dependency-injection-container/get-node-manager";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import Spinner from "@src/components/atoms/spinner/spinner";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import { useI18N } from "@src/language/language-context";
import OtaUpdateView from "@src/components/organisms/ota-update-view/ota-update-view";

export const NodeManagerPage = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const nodeManagerActiveView = useSelector(
    (state: RootState) => state.yourSlice.nodeManagerActiveView,
  );
  const [isMachineStructureLoaded, setIsMachineStructureLoaded] =
    useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const texts = {
    componentNotFound: i18n("pages.nodeManager.componentNotFound"),
    loadingError: i18n("pages.nodeManager.loadingError"),
  };

  const getComponentByActiveView = (
    nodeManagerActiveView: string,
  ): JSX.Element => {
    const components: Record<string, React.ComponentType<any>> = {
      treeStructure: TreeStructure,
      conditionMonitoring: AlarmList,
      shotDataTable: ShotTable,
      oneShot: OneShot,
      dashboard: BlueprintDashboard,
      machineSettings: OtaUpdateView,
    };

    const Component = components[nodeManagerActiveView];
    return Component ? <Component /> : <div>{texts.componentNotFound}</div>;
  };

  const nodeManager = getNodeManager();

  useEffect(() => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "sourceInfoDrawerModal",
      }),
    );

    let isComponentMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;

    const loadStructures = async () => {
      const logger = getLogger();

      try {
        // first load machine structure (blocking) and dashboard data
        // this will populate the machineStructureNodeset ASAP
        const savedStructureWithKeys =
          await nodeManager.loadMachineStructureAndDashboards(signal);

        // only update state if component is still mounted
        if (!isComponentMounted) return;

        // now ready to render with machine structure data
        // InitialStructurePanel should show a loading spinner while waiting
        setIsMachineStructureLoaded(true);
        setLoadError(null);

        // finally, load initial structure in background (slow, non-blocking)
        // components listen to initialStructureNodeset changes via observers
        // and automatically update when data arrives
        nodeManager.loadInitialStructureAsyncInBackground(
          savedStructureWithKeys,
          signal,
        );
      } catch (error) {
        console.error("NodeManagerPage: Failed to load structures", error);
        logger.debug("NodeManagerPage: Failed to load structures", {
          error: error instanceof Error ? error.message : String(error),
        });
        if (isComponentMounted) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Unknown error loading structures";
          setLoadError(errorMessage);
          setIsMachineStructureLoaded(false);
        }
      }
    };

    loadStructures();
    return () => {
      isComponentMounted = false;
      controller.abort();
    };
  }, [dispatch, nodeManager]);

  return (
    <MainContainer>
      {loadError ? (
        <>
          <Spacer16 />
          <Spacer16 />
          <Spacer16 />
          <Spacer16 />
          <RoundBorderComponentContainer>
            <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
              {texts.loadingError}: {loadError}
            </div>
          </RoundBorderComponentContainer>
        </>
      ) : !isMachineStructureLoaded ? (
        <>
          <Spacer16 />
          <Spacer16 />
          <Spacer16 />
          <Spacer16 />
          <RoundBorderComponentContainer>
            <Spinner />
          </RoundBorderComponentContainer>
        </>
      ) : (
        <>
          <Spacer16 />
          <NodeManagerInfoBarPillsNav />
          <Spacer16 />
          {nodeManagerActiveView === "machineSettings" ? (
            getComponentByActiveView(nodeManagerActiveView)
          ) : (
            <RoundBorderComponentContainer>
              {getComponentByActiveView(nodeManagerActiveView)}
            </RoundBorderComponentContainer>
          )}
        </>
      )}
    </MainContainer>
  );
};
